import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Bell, CheckCheck } from 'lucide-react';
import { clsx } from 'clsx';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent
} from '@/components/ui/DropdownMenu';
import { useErwaehnungen, useErwaehnungGelesen, type Erwaehnung } from '@/hooks/useErwaehnungen';
import { anzeigeNummer } from '@/lib/bestellnummer';
import { datumZeitDe, relativZeitDe } from '@/lib/format';
import { FARBE_NUTZER_FALLBACK } from '@/theme';
import { FOKUS_RING } from '@/lib/fokus';

// Die Glocke im Kopfbereich.
//
// Sie gehört zur AKTIVEN Sitzung, nicht zum Gerät: am gemeinsamen Tablet
// wechselt mit dem Nutzer auch die Glocke. Dafür sorgt der Anmeldename im
// Abfrageschlüssel (siehe useErwaehnungen) zusammen mit dem
// Sitzungskanal, der bei einem Wechsel in irgendeinem Fenster alle
// Abfragen neu lädt (B88).
//
// Kein eigenes Polling: der Fokus-Refetch der Anwendung trägt das. Und
// bewusst kein Push und keine Mail — das steht im Rückstand und kann
// später auf denselben Tabellen aufsetzen.

type Props = {
  /**
   * Wo die Glocke hängt.
   *
   * `kopf` — als Symbol in der Kopfzeile (Schreibtisch, Tablet).
   * `leiste` — als Knopf mit Beschriftung in der Bottom-Navigation.
   *
   * Immer nur EINE davon steht im Baum (siehe hooks/useLayout.ts): der
   * Anker `glocke` muss eindeutig bleiben, sonst zeigt die Tour womöglich
   * auf die unsichtbare Zwillingsschwester.
   */
  variante?: 'kopf' | 'leiste';
};

export function Glocke({ variante = 'kopf' }: Props) {
  const [offen, setOffen] = useState(false);
  const q = useErwaehnungen();
  const gelesen = useErwaehnungGelesen();
  const navigate = useNavigate();

  const eintraege = q.data?.erwaehnungen ?? [];
  const ungelesen = q.data?.ungelesen ?? 0;

  function oeffnen(e: Erwaehnung): void {
    setOffen(false);
    if (e.gelesen_am === null) gelesen.mutate({ id: e.id });
    void navigate({
      to: '/bestellungen/$bestellnr',
      params: { bestellnr: e.bestellnr }
    });
  }

  const beschriftung =
    ungelesen === 0 ? 'Erwähnungen — nichts Neues' : `Erwähnungen — ${ungelesen} ungelesen`;

  // Die Zahl ist die Nachricht — deshalb ein lesbares Feld und kein bloßer
  // Punkt. Coral ist für Warnungen reserviert; eine Erwähnung ist keine,
  // sie ist eine Bitte. Also Teal.
  const abzeichen = ungelesen > 0 && (
    <span
      className={clsx(
        'absolute inline-flex min-w-[1.1rem] items-center justify-center rounded-full bg-akzent px-1 text-[0.65rem] font-medium tabular-nums text-cream',
        variante === 'kopf' ? 'right-1 top-1' : 'right-[calc(50%-1.25rem)] top-1'
      )}
    >
      {ungelesen > 99 ? '99+' : ungelesen}
    </span>
  );

  return (
    <DropdownMenu open={offen} onOpenChange={setOffen}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          data-tour="glocke"
          aria-label={beschriftung}
          className={clsx(
            `relative text-ink-soft hover:bg-cream-dark hover:text-akzent ${FOKUS_RING}`,
            variante === 'kopf'
              ? 'inline-flex h-11 w-11 items-center justify-center rounded-md'
              : 'flex min-h-[56px] min-w-[56px] flex-1 flex-col items-center justify-center gap-0.5 rounded-md px-2 py-1'
          )}
        >
          <Bell className={variante === 'kopf' ? 'h-5 w-5' : 'h-6 w-6'} />
          {variante === 'leiste' && (
            <span className="text-[0.65rem] leading-none">Glocke</span>
          )}
          {abzeichen}
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent className="max-h-[70vh] w-[22rem] overflow-y-auto">
        <div className="flex items-center justify-between px-3 py-2">
          <span className="text-xs uppercase tracking-wide text-ink-faint">Erwähnungen</span>
          {ungelesen > 0 && (
            <button
              type="button"
              onClick={() => gelesen.mutate({ alle: true })}
              className="inline-flex min-h-[44px] items-center gap-1.5 rounded px-2 text-xs text-ink-soft hover:text-akzent"
            >
              <CheckCheck className="h-3.5 w-3.5" />
              Alle als gelesen
            </button>
          )}
        </div>

        {eintraege.length === 0 ? (
          <p className="px-3 pb-3 text-sm text-ink-faint">
            Niemand hat dich erwähnt.
          </p>
        ) : (
          <ul>
            {eintraege.map((e) => (
              <li key={e.id}>
                <button
                  type="button"
                  onClick={() => oeffnen(e)}
                  className={clsx(
                    'flex min-h-[44px] w-full flex-col items-start gap-0.5 border-l-2 px-3 py-2 text-left hover:bg-cream-dark',
                    e.gelesen_am === null
                      ? 'border-akzent bg-akzent-sanft/40'
                      : 'border-transparent'
                  )}
                >
                  <span className="flex w-full items-baseline gap-1.5 text-xs">
                    <span
                      aria-hidden
                      className="inline-block h-2 w-2 shrink-0 self-center rounded-full"
                      style={{ backgroundColor: e.von.farbe ?? FARBE_NUTZER_FALLBACK }}
                    />
                    <span className="font-medium text-ink">{e.von.kuerzel}</span>
                    <span className="text-ink-soft">hat dich erwähnt</span>
                    <span
                      className="ml-auto shrink-0 text-ink-faint"
                      title={datumZeitDe(e.erstellt_am)}
                    >
                      {relativZeitDe(e.erstellt_am)}
                    </span>
                  </span>
                  {/* Bestellnummer, und bei einer Positions-Notiz das
                      Buch dazu: bei fünf Positionen sagt die Nummer
                      allein nicht, worum es geht. Der Titel bricht
                      nicht um — die Zeile bleibt eine Zeile. */}
                  <span className="flex w-full min-w-0 items-baseline gap-1 font-serif text-xs text-akzent">
                    <span className="shrink-0">{anzeigeNummer(e)}</span>
                    {e.position !== null && (
                      <span className="truncate text-ink-soft">
                        · Position „{e.position.titel}“
                      </span>
                    )}
                  </span>
                  {/* Der Auszug ist eine bewusste Spiegelung des
                      Quelltextes: er zeigt, worum es ging, auch wenn die
                      Notiz danach umgeschrieben wurde. Maßgeblich bleibt
                      die Bestellung — dorthin führt der Klick. */}
                  <span className="line-clamp-2 text-xs italic text-ink-soft">
                    „{e.textauszug}“
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
